import React from 'react'
import { motion } from 'framer-motion'
import { Star, ThumbsUp, MessageCircle, ArrowRight, Headphones, Music, Mic } from 'lucide-react'

const ReviewsSection = () => {
  const reviews = [
    {
      id: 1,
      title: 'Shure SM58 - The Workhorse Microphone',
      author: 'Eddie Perry',
      authorRole: 'Audio Engineer',
      rating: 5,
      excerpt: 'After 15 years in the business, this mic still amazes me. Perfect for live vocals, incredibly durable...',
      image: 'https://images.pexels.com/photos/164938/pexels-photo-164938.jpeg?auto=compress&cs=tinysrgb&w=600',
      category: 'Gear Review',
      likes: 234,
      comments: 45,
      readTime: '5 min',
      featured: true,
    },
    {
      id: 2,
      title: 'The Wave - Venue Spotlight',
      author: 'Sarah Mitchell',
      authorRole: 'Local Artist',
      rating: 4.5,
      excerpt: 'Intimate setting with incredible acoustics. The staff treats musicians like family...',
      image: 'https://images.pexels.com/photos/1540406/pexels-photo-1540406.jpeg?auto=compress&cs=tinysrgb&w=600',
      category: 'Venue Review',
      likes: 189,
      comments: 32,
      readTime: '3 min',
    },
    {
      id: 3,
      title: 'Thunder Road Live at Lava Tonic',
      author: 'Music Wichita',
      authorRole: 'Music Blog',
      rating: 4.8,
      excerpt: 'High-energy performance that had the crowd on their feet from start to finish...',
      image: 'https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?auto=compress&cs=tinysrgb&w=600',
      category: 'Live Review',
      likes: 156,
      comments: 28,
      readTime: '4 min',
    },
  ]

  const categories = [
    { name: 'Gear Reviews', icon: Headphones, count: 45, color: 'from-le-accent to-purple-600' },
    { name: 'Artist Spotlights', icon: Music, count: 32, color: 'from-le-accent-2 to-blue-600' },
    { name: 'Venue Reviews', icon: Mic, count: 28, color: 'from-le-amber to-orange-600' },
  ]

  return (
    <section className="section-padding py-20">
      <div className="container-max">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-6">
            Reviews &
            <span className="text-gradient block">Spotlights</span>
          </h2>
          <p className="text-xl text-white/70 max-w-3xl mx-auto mb-8">
            In-depth reviews of gear, venues, and artists from Wichita's music community and industry experts.
          </p>

          {/* Review Categories */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {categories.map((category, index) => {
              const Icon = category.icon
              return (
                <motion.div
                  key={category.name}
                  className="glass-card p-6 hover:bg-white/10 transition-all duration-300 cursor-pointer group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${category.color} rounded-2xl flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform duration-300`}>
                    <Icon size={28} className="text-white" />
                  </div>
                  <h3 className="font-display font-semibold text-white mb-2 group-hover:text-le-accent transition-colors duration-300">
                    {category.name}
                  </h3>
                  <p className="text-white/60">{category.count} reviews</p>
                </motion.div>
              )
            })}
          </div>
        </motion.div>

        {/* Featured Reviews */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {reviews.map((review, index) => (
            <motion.article
              key={review.id}
              className={`glass-card overflow-hidden hover:bg-white/10 transition-all duration-300 cursor-pointer group ${
                review.featured ? 'lg:col-span-2' : ''
              }`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
            >
              {review.featured && (
                <div className="absolute top-4 left-4 z-10 bg-gradient-to-r from-le-accent to-le-accent-2 text-white text-xs px-3 py-1 rounded-full font-medium">
                  Editor's Pick
                </div>
              )}

              <div className="relative h-48 overflow-hidden">
                <img
                  src={review.image}
                  alt={review.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center justify-between text-white text-sm mb-2">
                    <span className="bg-black/40 backdrop-blur-sm px-2 py-1 rounded-full">
                      {review.category}
                    </span>
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={14}
                          className={`${
                            i < Math.floor(review.rating)
                              ? 'text-yellow-400 fill-current'
                              : 'text-white/40'
                          }`}
                        />
                      ))}
                      <span className="ml-1">{review.rating}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <h3 className="font-display font-semibold text-xl text-white mb-3 group-hover:text-le-accent transition-colors duration-300 leading-tight">
                  {review.title}
                </h3>

                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-le-accent to-le-accent-2 rounded-full flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {review.author.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium text-white">{review.author}</div>
                    <div className="text-sm text-white/60">{review.authorRole}</div>
                  </div>
                </div>

                <p className="text-white/70 mb-6 leading-relaxed">
                  {review.excerpt}
                </p>

                <div className="flex items-center justify-between text-sm text-white/60 mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                      <ThumbsUp size={14} />
                      <span>{review.likes}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <MessageCircle size={14} />
                      <span>{review.comments}</span>
                    </div>
                  </div>
                  <span>{review.readTime} read</span>
                </div>

                <button className="w-full btn-secondary text-sm py-2">
                  Read Full Review
                </button>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <button className="btn-secondary text-lg px-8 py-4 flex items-center space-x-2 mx-auto">
            <span>Read All Reviews</span>
            <ArrowRight size={18} />
          </button>
        </motion.div>
      </div>
    </section>
  )
}

export default ReviewsSection